import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Card, Spinner } from '@shared';
import { AddressFields, type AddressFormData } from '@/features/organization/AddressFields';
import { useCountryOptions } from '@/features/organization/useCountryOptions';
import { useTranslations } from '@/i18n/client';
import type { OrganizationApi } from '@/services/api/organization-api';
import type { AddressDto, AddressRequest } from '@/services/api/types';

import { useOrganizationAddresses } from './useOrganizationAddresses';
import { useOrganizationSettings } from './useOrganizationSettings';

export interface OrganizationAddressesSectionProps {
  /** Test-only injection point for a mock OrganizationApi. */
  organization?: OrganizationApi;
  embedded?: boolean;
}

const EMPTY_ADDRESS_FORM: AddressFormData = {
  companyName: '',
  street: '',
  zip: '',
  city: '',
  countryID: '',
  phone: '',
  email: '',
  vat: '',
  isBillingAddress: false,
  isShippingAddress: false,
};

function toAddressRequest(data: AddressFormData, displayName: string): AddressRequest {
  return {
    name: displayName.trim(),
    street: data.street.trim(),
    zip: data.zip.trim(),
    city: data.city.trim(),
    countryID: data.countryID.trim(),
    phone: data.phone.trim(),
    email: data.email.trim(),
    vat: data.vat.trim(),
    isBillingAddress: data.isBillingAddress,
    isShippingAddress: data.isShippingAddress,
  };
}

function toAddressFormData(address: AddressDto): AddressFormData {
  return {
    companyName: '',
    street: address.street,
    zip: address.zip,
    city: address.city,
    countryID: address.countryID,
    phone: address.phone,
    email: address.email,
    vat: address.vat,
    isBillingAddress: address.isBillingAddress,
    isShippingAddress: address.isShippingAddress,
  };
}

export function OrganizationAddressesSection({
  organization,
  embedded = false,
}: OrganizationAddressesSectionProps) {
  const { data, loading, loadError, saving, saveError, add, remove, update, clearSaveError } =
    useOrganizationAddresses(organization);
  const { options: countryOptions } = useCountryOptions();
  const { data: orgSettings } = useOrganizationSettings(organization);
  const t = useTranslations('settings');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const sectionClassName = embedded ? 'py-8' : 'md-dashboard__card';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddressFormData>({ defaultValues: EMPTY_ADDRESS_FORM });

  function openAddForm(): void {
    clearSaveError();
    reset(EMPTY_ADDRESS_FORM);
    setEditingId(null);
    setFormOpen(true);
  }

  function openEditForm(address: AddressDto): void {
    clearSaveError();
    reset(toAddressFormData(address));
    setEditingId(address.id);
    setFormOpen(true);
  }

  function closeForm(): void {
    clearSaveError();
    reset(EMPTY_ADDRESS_FORM);
    setEditingId(null);
    setFormOpen(false);
  }

  async function onSubmit(formData: AddressFormData): Promise<void> {
    const request = toAddressRequest(formData, orgSettings?.displayName ?? '');
    const success = editingId ? await update(editingId, request) : await add(request);
    if (success) {
      reset(EMPTY_ADDRESS_FORM);
      setEditingId(null);
      setFormOpen(false);
    }
  }

  async function onDelete(address: AddressDto): Promise<void> {
    if (!window.confirm(t('addresses.deleteConfirm'))) {
      return;
    }
    await remove(address.id);
  }

  if (loading) {
    return (
      <section className={sectionClassName}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner label={t('addresses.title')} />
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className={sectionClassName}>
        <Alert variant="error">{tErrors(`api.${loadError}`)}</Alert>
      </section>
    );
  }

  return (
    <section className={sectionClassName}>
      <h2 className={embedded ? 'm-0 mb-1.5 text-[1.35rem]' : undefined}>{t('addresses.title')}</h2>
      <p className={embedded ? 'm-0 mb-6 leading-[1.6] text-[var(--zs-muted)]' : undefined}>
        {t('addresses.subtitle')}
      </p>

      {data.length === 0 ? <p>{t('addresses.emptyMessage')}</p> : null}

      {data.length > 0 ? (
        <div className="flex flex-col gap-4">
          {data.map((address) => (
            <Card
              key={address.id}
              title={address.name}
              actions={
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saving}
                    onClick={() => openEditForm(address)}
                  >
                    {t('addresses.editButton')}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={saving}
                    onClick={() => void onDelete(address)}
                  >
                    {t('addresses.deleteButton')}
                  </Button>
                </>
              }
            >
              <p>{address.street}</p>
              <p>
                {address.zip} {address.city}
              </p>
              <p>{address.country}</p>
              {address.phone ? <p>{address.phone}</p> : null}
              {address.email ? <p>{address.email}</p> : null}
              {address.isBillingAddress ? <span>{t('addresses.billingAddressLabel')}</span> : null}
              {address.isShippingAddress ? <span>{t('addresses.shippingAddressLabel')}</span> : null}
            </Card>
          ))}
        </div>
      ) : null}

      {formOpen ? (
        <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {saveError ? <Alert variant="error">{tErrors(`api.${saveError}`)}</Alert> : null}

          <fieldset disabled={saving} style={{ border: 'none', padding: 0, margin: 0 }}>
            <AddressFields
              register={register}
              errors={errors}
              translationNamespace="settings"
              hideCompanyName
              countryOptions={countryOptions}
            />

            <Button type="submit" loading={saving} loadingLabel={tStatus('working')}>
              {t('addresses.saveButton')}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={closeForm}>
              {t('addresses.cancelButton')}
            </Button>
          </fieldset>
        </form>
      ) : (
        <Button type="button" className="mt-4" onClick={openAddForm}>
          {t('addresses.addButton')}
        </Button>
      )}
    </section>
  );
}
